import React, { useEffect, useState, useMemo, useRef } from 'react';
import { COLORS } from '../assets/colors';
import WelcomeCard from '../components/Converse/WelcomeCard';
import SearchBar from '../components/Converse/SearchBar';
import ChatComponent from '../components/Converse/ChatComponent';
import {
    PieChartOutlineOutlined,
    SearchOutlined,
    SignalCellularAltOutlined,
    TrendingUpOutlined,
    AutoAwesomeOutlined,
    KeyboardAltOutlined,
} from '@mui/icons-material';
import { useChatMessages, useChatInteract, sessionState, useChatSession } from '@chainlit/react-client';
import { useRecoilValue } from 'recoil';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ReplayIcon from '@mui/icons-material/Replay';
import {
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Alert,
    CircularProgress,
    Box
} from '@mui/material';

const styles = {
    title: {
        fontWeight: 600,
        fontFamily: 'Inter, Roboto',
        background: 'linear-gradient(to right, #FC6767 , #EC008C)',
        WebkitBackgroundClip: 'text',
        color: 'transparent',
        textAlign: 'center',
        fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
    },
    subtitle: {
        mb: 4,
        textAlign: 'center',
        color: COLORS.GRAY_SUBTITLE,
        fontSize: { xs: '0.875rem', sm: '1rem' },
    },
    disclaimer: {
        color: COLORS.GRAY,
        my: 1,
        textAlign: 'center',
        fontSize: { xs: '0.75rem', sm: '0.875rem' },
        // mx: '10%',
    },
};

function flattenMessages(messages, condition) {
    return messages.reduce((acc, node) => {
        if (condition(node)) {
            acc.push({
                ...node,
                waitForAnswer: node.waitForAnswer, // Inclure waitForAnswer
            });
        }
        if (node.steps?.length) {
            acc.push(...flattenMessages(node.steps, condition));
        }
        return acc;
    }, []);
}

const userEnv = {};

const Converse = () => {
    const [selectedText, setSelectedText] = useState('');
    const [lastUserMessage, setLastUserMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const { sendMessage, stopTask, clear } = useChatInteract();
    const { messages: chatMessages } = useChatMessages();
    const { connect } = useChatSession();
    const session = useRecoilValue(sessionState);

    const [errorModalOpen, setErrorModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [retrying, setRetrying] = useState(false);


    const handleStopTask = () => {
        setIsLoading(false)
        stopTask();
    };

    const flatMessages = useMemo(() => {
        // console.log('chatMessages:', chatMessages);
        return flattenMessages(chatMessages, (m) => m.type.includes("message"));
    }, [chatMessages]);




    const formattedMessages = flatMessages.map((msg, index, array) => {
        const isEmptySkeleton = msg.name !== 'user' && (msg.output === "" || msg.content === "");
        const nextMsg = array[index + 1];
        const shouldHide = isEmptySkeleton && nextMsg && nextMsg.name === msg.name;
        if (shouldHide) {
            return null;
        }
        const isUser = msg.name === 'user';
        let lastUserMessageIndex = -1;
        for (let i = index - 1; i >= 0; i--) {
            if (array[i]?.name === 'user') {
                lastUserMessageIndex = i;
                break;
            }
        }
        const isFirstBotMessage = !isUser && lastUserMessageIndex !== -1 &&
            !array.slice(lastUserMessageIndex + 1, index).some(m => m.name !== 'user' && (m.output || m.content));

        const isLastBotMessage = !isUser &&
            (index === array.length - 1 || array.slice(index + 1).every(m => m.name === 'user'));

        return {
            text: msg.output || msg.content,
            isUser,
            timestamp: new Date(msg.createdAt).toLocaleTimeString(),
            isLoading: msg.name !== 'user' && (msg.output === "" || msg.content === ""),
            actions: msg.actions || [],
            isFirstBotMessage,
            isLastBotMessage
        };
    }).filter(Boolean); // Filtre les valeurs null



    useEffect(() => {
        if (flatMessages.length > 0) {
            const lastMessage = flatMessages[flatMessages.length - 1];
            if (lastMessage.name !== 'user' && (lastMessage.output || lastMessage.content)) {
                setIsLoading(false);
            }
        }
    }, [flatMessages]);


    // useEffect(() => {
    //     if (session?.socket?.connected) {
    //         return;
    //     }
    //     fetch("http://localhost:80/custom-auth", {
    //         credentials: "include",
    //     })
    //         .then(() => {
    //             connect(
    //                 { userEnv }
    //                 // socketOptions: {
    //                 //     transports: ['polling', 'websocket'],
    //                 //     upgrade: true,
    //                 //     rememberUpgrade: true,
    //                 //     withCredentials: true,
    //                 //     timeout: 10000 // Augmenter le timeout
    //                 // }
    //             );
    //         });
    // }, [connect]);

    useEffect(() => {
        if (session?.socket?.connected) {
            return;
        }

        //fetch("https://conversequery-backend-376389693617.europe-west1.run.app/custom-auth", {
        fetch("https://conversequery-backend.intelcia.com/custom-auth", {
            credentials: "include",
        })
            .then(() => {
                connect(
                    { userEnv }
                );
            })
            .catch((error) => {
                console.log("Erreur de connexion au backend:", error);
                let message = "Impossible de se connecter au serveur";
                if (error.message) {
                    message = `Erreur de connexion: ${error.message}`;
                }
                setErrorMessage(message);
                setErrorModalOpen(true);
            });
    }, [connect]);


    const handleRetryConnection = () => {
        setRetrying(true);

        //fetch("https://conversequery-backend-376389693617.europe-west1.run.app/custom-auth", {
        fetch("https://conversequery-backend.intelcia.com/custom-auth", {
            credentials: "include",
        })
            .then(() => {
                connect({ userEnv });
                setErrorModalOpen(false);
                setRetrying(false);
            })
            .catch((error) => {
                console.log("Nouvel échec de connexion:", error);
                setRetrying(false);
                // Mettre à jour le message d'erreur si nécessaire
                setErrorMessage("La connexion a de nouveau échoué. Veuillez vérifier que le serveur est en cours d'exécution.");
            });
    };

    useEffect(() => {
        window.clearChat = clear;
        setSelectedText('')
        return () => {
            delete window.clearChat;
        };
    }, [clear]);


    const handleCardClick = (text) => {
        setSelectedText(text);
        setLastUserMessage(text)
        setIsLoading(true);
        sendMessage({
            name: 'user',
            type: 'user_message',
            output: text,
        });
    };

    const handleSendMessage = (message) => {
        setSelectedText(message);
        setLastUserMessage(message)
        setIsLoading(true);
        sendMessage({
            name: 'user',
            type: 'user_message',
            output: message,
        });
    };

    const handleCallEdition = () => {
        if (typeof window.callEditAction === 'function') {
            window.callEditAction(); // Appel de l'action "edit"
        } else {
            console.error("window.callEditAction is not a function");
        }
    };

    // Données des cartes
    const cardData = [
        { icon: <PieChartOutlineOutlined />, text: 'Quelles est la productivité des 2 dernières semaines pour le projet ftth ae' },
        { icon: <SearchOutlined />, text: 'C’est quoi la définition du trp' },
        { icon: <SignalCellularAltOutlined />, text: 'Donne moi le taux aux global hier pour le projet ftth ae et compare le avec son  objectif' },
        { icon: <TrendingUpOutlined />, text: 'Peux-tu me dire si je suis à l’objectif QS pour le projet fttb ae pour ce mois en cours' },
        { icon: <AutoAwesomeOutlined />, text: 'Donne moi les tranches ratées d aujourdhui pour le projet front co ae' },
        { icon: <KeyboardAltOutlined />, text: 'Analyse les tranches de 8h et 10h30 d’aujourdhui pour le projet fttb ae' },
    ];
    return (
        <Box sx={{ alignItems: 'center', }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', px: 4, height: '50%' }}>
                <Typography variant="h4" sx={{ ...styles.title }}>
                    Bienvenue Walid ENNACIRI
                </Typography>
                <Typography variant="subtitle1" sx={{ ...styles.subtitle }}>
                    N&apos;hésitez pas à me demander quoi que ce soit pour démarrer une conversation
                </Typography>

                {selectedText === '' ? (
                    <Box sx={{ mb: '9%' }}>
                        <Grid container justifyContent="center" spacing={2}>
                            {cardData.map((element, index) => (
                                <WelcomeCard
                                    key={index}
                                    index={index}
                                    element={element}
                                    onClick={() => handleCardClick(element.text)}
                                />
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    <ChatComponent messages={formattedMessages}
                        isLoading={isLoading}
                        onSendMessage={handleSendMessage}
                        lastUserMessage={lastUserMessage}
                        handleCallEdition={handleCallEdition} />
                )}
            </Box>

            <Box sx={{
                position: 'fixed',
                bottom: 0,
                width: { xs: '90%', sm: '80%' },
                alignSelf: 'center',
                justifyContent: 'center',
                alignItems: 'center',
                pt: 1,
                bgcolor: COLORS.WHITE,
                // zIndex: 9999, // Optionnel : pour s'assurer que la Box est au-dessus des autres éléments
            }}>
                <SearchBar onSendMessage={handleSendMessage} isLoading={isLoading} handleStopTask={handleStopTask} />
                <Box sx={{ width: '60%', mx: '15%' }}>
                    <Typography variant="body2" sx={{ ...styles.disclaimer }}>
                        ConverseQuery® peut faire des erreurs. Vérifiez les informations importantes.
                    </Typography>
                </Box>
            </Box>
            <Dialog
                open={errorModalOpen}
                onClose={() => setErrorModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
                        padding: '8px'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ErrorOutlineIcon color="error" fontSize="large" />
                    <Typography variant="h6" component="div">
                        Erreur de connexion
                    </Typography>
                </DialogTitle>

                <DialogContent>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMessage}
                    </Alert>

                    <Typography variant="body2" color="text.secondary">
                        Le serveur backend n'est pas accessible. Cela peut être dû à l'une des raisons suivantes :
                    </Typography>

                    <Box component="ul" sx={{ pl: 2, mt: 1 }}>
                        <Typography component="li" variant="body2">
                            Le serveur n'est pas en cours d'exécution.
                        </Typography>
                        <Typography component="li" variant="body2">
                            Votre connexion réseau est instable.
                        </Typography>
                        <Typography component="li" variant="body2">
                            Un pare-feu bloque la connexion.
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 3, pt: 1, justifyContent: 'space-between' }}>
                    <Button
                        onClick={() => setErrorModalOpen(false)}
                        color="inherit"
                        variant="text"
                    >
                        Fermer
                    </Button>

                    <Button
                        onClick={handleRetryConnection}
                        variant="contained"
                        color="primary"
                        startIcon={retrying ? <CircularProgress size={18} color="inherit" /> : <ReplayIcon />}
                        disabled={retrying}
                        sx={{
                            bgcolor: COLORS.PINK_INTELCIA,
                            borderRadius: '50px',
                            '&:hover': {
                                cursor: 'pointer',
                            },
                        }}
                    >
                        {retrying ? "Tentative..." : "Réessayer"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Converse;